import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChart as PieIcon } from "lucide-react";
import InfoTip from "./InfoTip";
import type { LearningMixSlice } from "@/lib/parent/commandMetrics";

const COLORS = ["hsl(var(--primary))", "hsl(var(--secondary))", "hsl(35 90% 55%)", "hsl(150 60% 50%)", "hsl(280 60% 65%)"];

interface Props {
  slices: LearningMixSlice[];
  total: number;
}

export default function LearningMixDonut({ slices, total }: Props) {
  const data = slices.filter((s) => s.value > 0);
  return (
    <section className="relative h-full overflow-hidden rounded-3xl border border-white/[0.06] bg-gradient-to-br from-card/80 via-card/60 to-card/40 p-5 backdrop-blur-2xl shadow-[0_24px_60px_-30px_hsl(var(--primary)/0.45),inset_0_1px_0_0_hsl(var(--foreground)/0.05)]">
      <div className="relative">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
            <PieIcon className="h-3.5 w-3.5" />
          </span>
          <h3 className="font-heading text-sm font-semibold tracking-tight text-foreground sm:text-base">Learning Mix</h3>
          <InfoTip title="Learning Mix" body="How your teen's recent learning splits across HeirWise topic areas." />
        </div>

        {total === 0 ? (
          <p className="mt-6 text-center text-xs text-muted-foreground">No activity yet to chart.</p>
        ) : (
          <div className="mt-3 flex items-center gap-3">
            <div className="relative h-32 w-32 shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data} dataKey="value" nameKey="label" innerRadius={36} outerRadius={56} stroke="hsl(var(--card))" strokeWidth={2} paddingAngle={2}>
                    {data.map((s, i) => <Cell key={s.id} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as LearningMixSlice;
                      return (
                        <div className="rounded-xl border border-primary/25 bg-card/90 px-3 py-2 text-[11px] backdrop-blur-xl">
                          <p className="font-semibold text-foreground">{p.label}: {p.pct}%</p>
                          <p className="text-muted-foreground">{p.value} learning moment{p.value === 1 ? "" : "s"}</p>
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total</p>
                <p className="font-heading text-xl font-semibold text-foreground">{total}</p>
              </div>
            </div>
            <ul className="flex-1 space-y-1 text-[11px]">
              {slices.map((s, i) => (
                <li key={s.id} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-1.5 text-foreground/90">
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="truncate">{s.label}</span>
                  </span>
                  <span className="shrink-0 font-medium text-muted-foreground">{s.pct}%</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}